import React, { useCallback, useEffect, useState } from "react";
import { differenceInSeconds } from "date-fns";

interface TimerCircleProps {
  dueTime: Date;
  size?: number;
  strokeWidth?: number;
  className?: string;
}

export default function OrderTimer({
  dueTime,
  size = 200,
  strokeWidth = 10,
  className,
}: TimerCircleProps) {
  const calculateTimeLeft = useCallback(() => {
    const now = new Date();
    const diff = differenceInSeconds(dueTime, now);
    return diff > 0 ? diff : 0;
  }, [dueTime]);

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());
  const [isPulsating, setIsPulsating] = useState(false);

  // Define one hour in seconds
  const oneHourInSeconds = 3600;

  // Compute progress (0 to 1): 0 means no time left, 1 means full hour left
  // Progress only starts decreasing when timeLeft is less than 1 hour
  const progress =
    timeLeft === 0
      ? 1
      : timeLeft < oneHourInSeconds
        ? timeLeft / oneHourInSeconds
        : 1;

  // Calculate color based on time left
  const getColorForTimeLeft = (timeLeftInSeconds: number) => {
    if (timeLeftInSeconds === 0) {
      return "#ff0000"; // Red when time is up
    } else if (timeLeftInSeconds > 30 * 60) {
      // More than 30 minutes left: Green
      return "#00cc00"; // Green
    } else if (timeLeftInSeconds > 15 * 60) {
      // Between 30 and 15 minutes left: Yellow
      return "#ffcc00"; // Yellow
    } else if (timeLeftInSeconds > 5 * 60) {
      // Between 15 and 5 minutes left: Orange
      return "#ff9900"; // Orange
    } else {
      // Less than 5 minutes left: Red
      return "#ff0000"; // Red
    }
  };

  const dynamicColor = getColorForTimeLeft(timeLeft);

  useEffect(() => {
    // Start pulsating when time is up
    if (timeLeft === 0) {
      setIsPulsating(true);
    } else {
      setIsPulsating(false);
    }

    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(interval);
  }, [timeLeft, dueTime, calculateTimeLeft]);

  // Calculate the maximum stroke width during pulsation
  const maxStrokeWidth = isPulsating ? strokeWidth * 1.5 : strokeWidth;

  // Add padding to ensure the pulsating stroke doesn't get cut off
  const padding = maxStrokeWidth;
  const svgSize = size + padding * 2;

  const center = svgSize / 2;
  const radius = size / 2 - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - progress);

  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor((timeLeft % 3600) / 60);

  const formatTime = (hrs: number, mins: number) => {
    if (timeLeft === 0) {
      return "00:00";
    }

    const h = hrs < 10 ? `0${hrs}` : hrs;
    const m = mins < 10 ? `0${mins}` : mins;

    if (hrs > 0) {
      return `${h}:${m}`;
    } else {
      return `00:${m}`;
    }
  };

  return (
    <div
      className={className}
      style={{
        color: "#000000",
        position: "relative",
        width: size,
        height: size,
        overflow: "visible",
      }}
    >
      <svg
        width={svgSize}
        height={svgSize}
        style={{
          position: "absolute",
          top: -padding,
          left: -padding,
          overflow: "visible",
        }}
      >
        {/* Background white circle */}
        <circle
          cx={center}
          cy={center}
          r={radius + strokeWidth / 2}
          fill="white"
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke="#e6e6e6"
          strokeWidth={strokeWidth}
          fill="none"
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          stroke={dynamicColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          style={{
            transition: "stroke-dashoffset 1s linear, stroke 1s linear",
            transform: "rotate(-90deg)",
            transformOrigin: "50% 50%",
            animation: isPulsating ? "pulse 1.5s infinite" : "none",
          }}
        />
      </svg>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: size,
          height: size,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: size * 0.2,
          fontFamily: "sans-serif",
        }}
      >
        {formatTime(hours, minutes)}
      </div>
      <style>{`
        @keyframes pulse {
          0% {
            stroke-opacity: 1;
            stroke-width: ${strokeWidth}px;
          }
          50% {
            stroke-opacity: 0.5;
            stroke-width: ${strokeWidth * 1.5}px;
          }
          100% {
            stroke-opacity: 1;
            stroke-width: ${strokeWidth}px;
          }
        }
      `}</style>
    </div>
  );
}
